/**
 * Hook: PATCH /api/t/{slug}/notes/{candidate_id}/{note_id} — per-note edit
 * (P2 demo-friction fix, founder-authorized 2026-06-16; reverses the prior
 * append-only Sec HARD-7 stance).
 *
 * Backend updates `content`, sets `edited_at`, and writes an `activity_log`
 * row. Authz is author-or-admin and enforced server-side; a soft-deleted
 * note cannot be edited (404). The FE shows the affordance only to users
 * who can act (cosmetic gate).
 *
 * Cache strategy: replace the row in-place across every page of the
 * `useNotes` infinite-query cache from the server response.
 */
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
  type InfiniteData,
} from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import { apiClient } from "@/lib/apiClient";
import { useToastStore } from "@/stores/useToastStore";
import type { NoteOut, NotesPage } from "./useNotes";

interface UpdateNoteVars {
  candidate_id: string;
  note_id: string;
  content: string;
}

export function useUpdateNote(): UseMutationResult<
  NoteOut,
  ApiError,
  UpdateNoteVars
> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);

  return useMutation<NoteOut, ApiError, UpdateNoteVars>({
    mutationFn: async (vars) => {
      const r = await apiClient.patch<NoteOut>(
        `/api/t/${tenantSlug}/notes/${vars.candidate_id}/${vars.note_id}`,
        { content: vars.content },
      );
      return r.data;
    },
    onSuccess: (realRow, vars) => {
      const queryKey = [
        "notes",
        "by-candidate",
        tenantSlug,
        vars.candidate_id,
      ] as const;
      queryClient.setQueryData<InfiniteData<NotesPage, string | null>>(
        queryKey,
        (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            pages: prev.pages.map((page) => ({
              ...page,
              items: page.items.map((it) =>
                it.id === realRow.id ? realRow : it,
              ),
            })),
          };
        },
      );
      addToast("Note updated.", "success");
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.status === 403) {
          addToast("You can only edit your own notes.", "error");
          return;
        }
        if (err.status === 404) {
          addToast("Note not found. Refresh the page.", "error");
          return;
        }
        if (err.status === 422) {
          addToast("Note can't be empty.", "error");
          return;
        }
      }
      addToast("Couldn't update note. Try again.", "error");
    },
  });
}
